import { Component, EventEmitter, Input, input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators,ReactiveFormsModule, FormControl, AsyncValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { categories } from '../../../utils/select-options';
import { IUser } from '../../../../core/models/IUser';
import { CompanyService } from '../../../../core/services/company.service';
import { AddressAutocompleteService, AddressSuggestion } from '../../../services/address-autocomplete.service';
import { TvaService } from '../../../services/tva.service';
import { Observable, debounceTime, catchError, first, of, switchMap, map } from 'rxjs';
import { LogService } from '../../../../core/services/log.service';

@Component({
  selector: 'app-company-add',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule
  ],
  templateUrl: './company-add.html',
  styleUrl: './company-add.scss'
})
export class CompanyAdd {
  
  @Input() currentUser : IUser | null = null;
  @Output() companyAdded = new EventEmitter<any>();
  @Output() cancelAdd = new EventEmitter<void>();

  categories = categories.sort();
  companyForm!: FormGroup;
  addressCtrl = new FormControl('');
  suggestions: any[] = [];

  constructor(
    private fb: FormBuilder, 
    private companyService: CompanyService,
    private addressAutocompleteService: AddressAutocompleteService,
    private tvaService: TvaService,
    private logger: LogService
  ){}

  ngOnInit(): void {
    this.companyForm = this.fb.group({
      companyName: ['', Validators.required],
      companyNumber: ['', Validators.required, [this.tvaAsyncValidator()]],
      roleInCompany: [''],
      companyEmail: ['', [Validators.required, Validators.email]],
      companyPhoneNumber: ['', Validators.required],
      address: this.fb.group({
        street: [this.currentUser?.address.street, Validators.required],
        number: [this.currentUser?.address.number, [Validators.required, Validators.min(1)]],
        city: [this.currentUser?.address.city, Validators.required],
        zipCode: [this.currentUser?.address.zipCode, Validators.required],
        country: [this.currentUser?.address.country, Validators.required],
      }),
      location: this.fb.group({
        type: ['Point'],
        coordinates: [[]]
      }),
      category: ['', Validators.required],
      description: ['']
    });

    this.addressCtrl.setValue(`${this.currentUser?.address.street} ${this.currentUser?.address.number}, ${this.currentUser?.address.city}, ${this.currentUser?.address.country}`);

    this.addressCtrl.valueChanges
      .pipe(
        debounceTime(300),
        switchMap(value => this.addressAutocompleteService.getSuggestions(value || ""))
      )
      .subscribe(results => this.suggestions = results);
  }

  onSubmit(): void {
    if (this.companyForm.valid) {
      this.logger.log('Formulaire société: ' + JSON.stringify(this.companyForm.value));
      this.companyService.createCompany(this.companyForm.value).subscribe({
        next: (response) => {
          this.logger.log('Société créée avec succès: ' + JSON.stringify(response));
          this.companyAdded.emit({data: response.data, role: response.role});
        },
        error: (error) => {
          this.logger.error('Erreur lors de la création de la société:: ' + JSON.stringify(error));
        }
      });
    }
  }

  useUserAddress(): void {
    this.companyForm.get('company.address')?.patchValue(this.currentUser?.address);
    this.addressCtrl.setValue(`${this.currentUser?.address.street} ${this.currentUser?.address.number}, ${this.currentUser?.address.city}, ${this.currentUser?.address.country}`);
  }

  onCancel(): void {
    this.cancelAdd.emit();
  }

  selectSuggestion(s: AddressSuggestion) {
    // Extraire les morceaux utiles
    const street = s.text;
    const context = s.context || [];
    const city = context.find(c => c.id.startsWith('place'))?.text || '';
    const postcode = context.find(c => c.id.startsWith('postcode'))?.text || '';
    const country = context.find(c => c.id.startsWith('country'))?.text || '';

    // Met à jour le champ visible
    this.addressCtrl.setValue(s.place_name, { emitEvent: false });

    this.companyForm.get('address')?.patchValue({
      street,
      number: s.address ? parseInt(s.address) : 0,
      zipCode: postcode ? parseInt(postcode, 10) : 0,
      city,
      country
    });
    
    (this.companyForm.get('location') as any).location = s.geometry;

    this.suggestions = [];
  }

  tvaAsyncValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) return of(null);
      return this.tvaService.checkTva(control.value).pipe(
        map(result => {
          this.logger.log(`Résultat vérification TVA pour ${control.value}: ${JSON.stringify(result)}`);
          if (result?.isValid === false) {
            this.logger.warn(`TVA invalide: ${control.value}`, { tva: control.value });
            return { invalidTva: result.message ?? 'TVA invalide' };
          }
          this.logger.log(`TVA valide: ${control.value}`, { tva: control.value });
          return null;
        }),
        catchError(e => {
          this.logger.error(`Erreur lors de la vérification de la TVA: ${JSON.stringify(e)}`, { tva: control.value, error: e });
          return of({ tvaError: 'Impossible de vérifier la TVA' });
        }),
        first()
      );
    };
  }

}
